/* eslint-disable @typescript-eslint/no-empty-object-type */
import { get } from 'lodash'
import { createI18n, type I18n, type I18nOptions } from 'vue-i18n'
import { z } from 'zod'
import { useStorage } from '@library/composables'
import { LocalStorage } from '@library/enums/local-storage'
import { Locale } from '@library/enums/locale'
import { libraryEn, libraryZhCN, libraryZhTW } from './langs'
export { libraryEn, libraryZhCN, libraryZhTW }

export type BfI18n = ReturnType<typeof initI18n>

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AppI18n {
  // eslint-disable-next-line sonarjs/public-static-readonly
  static i18n: I18n<Record<Locale, unknown>, {}, {}, Locale, false>
}

export function initI18n<Messages extends Record<Locale, unknown>>(
  messages: Record<Locale, unknown>
): I18n<Messages, {}, {}, Locale, false> {
  const i18n: I18n<Messages, {}, {}, Locale, false> = createI18n({
    locale: getDefaultLocale(),
    fallbackLocale: getDefaultFallbackLocale(),
    legacy: false,
    messages: messages as I18nOptions['messages'],
    messageResolver: (obj, path) =>
      get(obj, path, '')?.replace(/@/g, "{'@'}") || null,
    warnHtmlMessage: false
  })

  AppI18n.i18n = i18n as I18n<Record<Locale, unknown>, {}, {}, Locale, false>

  return i18n
}

/**
 * 取得預設語系，優先順序如下
 * 1. 網址有加 ?lang=語系，且系統支援該語系時，使用該語系，否則使用 en（在 app.vue 實作此功能）
 * 2. Local Storage 有儲存語系，且系統支援該語系時，使用該語系，否則判斷第 3 點
 * 3. 瀏覽器語系為 zh 時，使用 zh，否則使用 en
 */
function getDefaultLocale(): Locale {
  const { getStorageData } = useStorage()
  const localStorageLang = getStorageData({
    key: LocalStorage.LOCALE,
    zodSchema: z.nativeEnum(Locale)
  })

  // 系統支援 Local Storage 所儲存的語系時，使用該語系
  if (localStorageLang) {
    return localStorageLang
  }

  // 瀏覽器語系為 zh 時，使用 zh，否則使用 en
  return navigator.language === Locale.ZH_TW ? navigator.language : Locale.EN
}

/** 取得無設定語系檔時，默認的語系 */
function getDefaultFallbackLocale(): Locale {
  return navigator.language === Locale.ZH_TW ? Locale.ZH_TW : Locale.EN
}
